import type { LucideIcon } from 'lucide-react';
import {
  Webhook,
  Shuffle,
  Globe,
  Layers,
  ArrowRightLeft,
  KeyRound,
  Search,
  GitBranch,
  Split,
  Repeat,
  GitMerge,
  Clock,
  RotateCw,
  FileDown,
  FileUp,
  ShieldCheck,
  Fingerprint,
  Code,
  StickyNote,
  OctagonX,
  Workflow,
} from 'lucide-react';
import type { FlowNode } from '../../types';

export type StepCategory = 'Trigger' | 'Data' | 'Logic' | 'Control' | 'Templates' | 'Documentation';

export type NodeStepType = FlowNode['type'];

export type StepTone = 'violet' | 'blue' | 'amber' | 'orange' | 'red' | 'slate';

/** Literal class strings per tone, so Tailwind's scanner sees every one of them. */
export const TONE_CLASSES: Record<StepTone, { text: string; tile: string }> = {
  violet: { text: 'text-tone-violet', tile: 'bg-tone-violet/10 text-tone-violet' },
  blue: { text: 'text-tone-blue', tile: 'bg-tone-blue/10 text-tone-blue' },
  amber: { text: 'text-tone-amber', tile: 'bg-tone-amber/10 text-tone-amber' },
  orange: { text: 'text-tone-orange', tile: 'bg-tone-orange/10 text-tone-orange' },
  red: { text: 'text-tone-red', tile: 'bg-tone-red/10 text-tone-red' },
  slate: { text: 'text-tone-slate', tile: 'bg-tone-slate/10 text-tone-slate' },
};

export interface StepDefinition {
  label: string;
  icon: LucideIcon;
  category: StepCategory;
  /** Shared by the canvas node, the step picker and the config panel. One per category. */
  tone: StepTone;
  /** Detailed explanation shown in the step picker's hover tooltip. */
  description: string;
  /** Node type to add. Omitted for preset entries. */
  type?: NodeStepType;
  /** Preset handler marker. Omitted for plain node entries. */
  presetId?: string;
}

export const CATEGORY_ORDER: StepCategory[] = ['Trigger', 'Data', 'Logic', 'Control', 'Templates', 'Documentation'];

export const STEP_TYPES: StepDefinition[] = [
  { type: 'webhook', label: 'Webhook', category: 'Trigger', tone: 'violet', icon: Webhook,
    description: 'Entry point of every flow. Receives the inbound HTTP request and exposes its body, headers, and query parameters throughout the flow via {{body.*}}, {{headers.*}}, and {{query.*}}.' },

  { type: 'transform', label: 'Transform', category: 'Data', tone: 'blue', icon: Shuffle,
    description: 'Reshapes data. The config is a JSON object whose string values are template expressions; the resolved object becomes this step\'s output.' },
  { type: 'http', label: 'HTTP Request', category: 'Data', tone: 'blue', icon: Globe,
    description: 'Makes an outbound HTTP call (URL, method, headers, body). Placeholders in the body are resolved and numbers/booleans/null are type-coerced. Response is available as {{<nodeId>.body}} and {{<nodeId>.status}}.' },
  { type: 'http-paginate', label: 'HTTP Paginate', category: 'Data', tone: 'blue', icon: Layers,
    description: 'Like HTTP Request, but automatically follows pages and aggregates the results across multiple requests into a single array output.' },
  { type: 'http-transfer', label: 'HTTP Transfer', category: 'Data', tone: 'blue', icon: ArrowRightLeft,
    description: 'Streams a file from a source URL straight into an upload to a target URL (multipart form or raw body) without storing it. The output holds only metadata, e.g. {{<nodeId>.target.status}}, {{<nodeId>.target.body}} and {{<nodeId>.bytesTransferred}}.' },
  { type: 'token-lookup', label: 'Token Lookup', category: 'Data', tone: 'blue', icon: KeyRound,
    description: 'Finds a stored OAuth token for a (customerId, tool) pair and auto-refreshes it if it expires within 60 seconds. Outputs accessToken, refreshToken, and expiresAt.' },
  { type: 'lookup', label: 'Lookup', category: 'Data', tone: 'blue', icon: Search,
    description: 'Maps an input value to an output value through a configured table (case-insensitive), with an optional default. Pure mapping — it does not branch.' },
  { type: 'watermark-read', label: 'Watermark Read', category: 'Data', tone: 'blue', icon: FileDown,
    description: 'Reads a stored watermark/cursor value (e.g. the last processed timestamp) so a flow can process data incrementally.' },
  { type: 'watermark-write', label: 'Watermark Write', category: 'Data', tone: 'blue', icon: FileUp,
    description: 'Writes or updates the watermark/cursor value so the next run resumes from where this one left off.' },

  { type: 'crypto', label: 'Crypto', category: 'Data', tone: 'blue', icon: Fingerprint,
    description: 'Hashes or encodes a value: SHA-256, HMAC-SHA-256, base64 or base64url, with optional lower/trim normalisation. Used to verify inbound webhook signatures over {{rawBody}} and to hash emails for APIs that only accept hashed identifiers.' },

  { type: 'code', label: 'Code', category: 'Data', tone: 'blue', icon: Code,
    description: 'Runs a sandboxed JavaScript function body against an explicitly mapped input object and returns the result as the step output. For reshaping the template engine cannot express: mapping arrays of objects, filtering, decimals, booleans from lookups. No network, files or timers.' },

  { type: 'eval', label: 'Eval', category: 'Logic', tone: 'amber', icon: GitBranch,
    description: 'Evaluates one or more conditions combined with AND/OR and branches to the next step through its "true" and "false" output handles.' },
  { type: 'switch', label: 'Switch', category: 'Logic', tone: 'amber', icon: Split,
    description: 'Routes execution to one of several cases based on an expression (or to "default" if none match). Each case has its own output handle.' },
  { type: 'foreach', label: 'For Each', category: 'Logic', tone: 'amber', icon: Repeat,
    description: 'Fans out execution over an array — one child execution is created per element and dispatched via the message bus. Iterations run concurrently, not sequentially.' },
  { type: 'subflow', label: 'Sub-Flow', category: 'Logic', tone: 'amber', icon: Workflow,
    description: 'Triggers exactly one child execution of a target flow (ForEach with N=1). An optional body template shapes the child payload; leave it empty to forward the parent body unchanged. Fire-and-forget.' },
  { type: 'join', label: 'Join', category: 'Logic', tone: 'amber', icon: GitMerge,
    description: 'Synchronisation point for parallel branches. Waits for all incoming branches to finish before continuing. Produces no output of its own.' },

  { type: 'wait', label: 'Wait', category: 'Control', tone: 'orange', icon: Clock,
    description: 'Pauses the execution for a configured number of seconds before continuing to the next step.' },
  { type: 'retry', label: 'Retry', category: 'Control', tone: 'orange', icon: RotateCw,
    description: 'Aborts the current execution and schedules a full retry of the flow with the same payload, using exponential backoff. Has no output handle.' },
  { type: 'fail', label: 'Fail', category: 'Control', tone: 'red', icon: OctagonX,
    description: 'Fails the execution on purpose with a templated message and optional code. Use it at the end of a branch that detects a state the flow must not continue from. Has no output handle.' },

  { presetId: 'http-check', label: 'HTTP Check', category: 'Templates', tone: 'amber', icon: ShieldCheck,
    description: 'Template: inserts an Eval step pre-filled with common HTTP checks (status == 200 and a non-empty body).' },

  { type: 'comment', label: 'Comment', category: 'Documentation', tone: 'slate', icon: StickyNote,
    description: 'A free-floating comment for documenting the flow. It has no connection points and is ignored during execution.' },
];

const STEP_BY_TYPE = new Map(
  STEP_TYPES.filter((s): s is StepDefinition & { type: NodeStepType } => s.type !== undefined).map(s => [s.type, s]),
);

export function stepDefinition(type: string): StepDefinition | undefined {
  return STEP_BY_TYPE.get(type as NodeStepType);
}

export const NODE_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  STEP_TYPES.filter((s): s is StepDefinition & { type: NodeStepType } => s.type !== undefined).map(s => [s.type, s.label]),
);
